class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.summaryElement = this.mainDetailsToggle.querySelector('summary');
    this.hoverMediaQuery = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
    this.hoverCloseTimer = null;
    this.closeFallbackTimer = null;
    this.closeAnimationHandler = null;
    this.previewTriggers = Array.from(
      this.querySelectorAll('[data-collection-preview-target], [data-learning-preview-target]')
    );
    this.previewPanels = Array.from(
      this.querySelectorAll('[data-collection-preview-panel], [data-learning-preview-panel]')
    );

    this.addEventListener('pointerenter', this.onPointerEnter.bind(this));
    this.addEventListener('pointerleave', this.onPointerLeave.bind(this));
    this.summaryElement.addEventListener('click', this.onSummaryClick.bind(this));

    this.previewTriggers.forEach((trigger) => {
      trigger.addEventListener('pointerenter', () => this.activatePreview(trigger));
      trigger.addEventListener('focus', () => this.activatePreview(trigger));
    });
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;
    this.mainDetailsToggle
      .querySelector('summary')
      .setAttribute('aria-expanded', this.mainDetailsToggle.open ? 'true' : 'false');

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  onPointerEnter() {
    if (!this.hoverMediaQuery.matches) return;

    window.clearTimeout(this.hoverCloseTimer);
    this.cancelCloseAnimation();
    this.closeSiblingMenus();
    this.mainDetailsToggle.setAttribute('open', '');
    this.summaryElement.setAttribute('aria-expanded', 'true');
  }

  onPointerLeave() {
    if (!this.hoverMediaQuery.matches) return;

    window.clearTimeout(this.hoverCloseTimer);
    this.hoverCloseTimer = window.setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    }, 140);
  }

  onSummaryClick(event) {
    if (!this.hoverMediaQuery.matches || !this.mainDetailsToggle.open) return;

    event.preventDefault();
    this.close();
  }

  activatePreview(trigger) {
    const targetId = trigger.dataset.collectionPreviewTarget || trigger.dataset.learningPreviewTarget;
    const nextPanel = this.previewPanels.find((panel) => panel.id === targetId);

    if (!nextPanel || nextPanel.classList.contains('is-active')) return;

    this.previewTriggers.forEach((item) => {
      const isActive = item === trigger;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });
    this.previewPanels.forEach((panel) => {
      panel.classList.remove('is-active');
      panel.hidden = panel !== nextPanel;
    });

    window.requestAnimationFrame(() => nextPanel.classList.add('is-active'));
  }

  cancelCloseAnimation() {
    window.clearTimeout(this.closeFallbackTimer);
    this.mainDetailsToggle.classList.remove('is-closing');

    if (this.closeAnimationHandler) {
      this.content.removeEventListener('animationend', this.closeAnimationHandler);
      this.closeAnimationHandler = null;
    }
  }

  close(immediate = false) {
    window.clearTimeout(this.hoverCloseTimer);

    if (!this.mainDetailsToggle.open) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (immediate || !this.hoverMediaQuery.matches || reduceMotion) {
      this.cancelCloseAnimation();
      super.close();
      return;
    }

    this.cancelCloseAnimation();
    this.mainDetailsToggle.classList.add('is-closing');
    this.summaryElement.setAttribute('aria-expanded', 'false');

    const finishClose = () => {
      this.cancelCloseAnimation();
      super.close();
    };

    this.closeAnimationHandler = (event) => {
      if (event.target === this.content && event.animationName === 'wood-header-menu-conceal') finishClose();
    };

    this.content.addEventListener('animationend', this.closeAnimationHandler);
    this.closeFallbackTimer = window.setTimeout(finishClose, 560);
  }

  closeSiblingMenus() {
    const siblingMenus = this.closest('.header__inline-menu')?.querySelectorAll('header-menu');

    siblingMenus?.forEach((menu) => {
      if (menu !== this) menu.close();
    });
  }
}

customElements.define('header-menu', HeaderMenu);
